<script setup lang="ts">
import Sonner from "~/components/ui/sonner/Sonner.vue";
import SplashScreen from "~/components/voice-ui/SplashScreen.vue";
import * as Sentry from '@sentry/nuxt'
import { defineAsyncComponent, onMounted, ref } from 'vue'
import { useAuth } from '~/composables/useAuth'
import { useUserStore } from '~/stores/user'
import { useGroupsSignalR } from '~/composables/useGroupsSignalR'
import { useUsersSignalR } from '~/composables/useUsersSignalR'
import { useUserStatus } from '~/composables/useUserStatus'
import { startIdleDetection } from '~/composables/useIdleDetection'
import { useUpdater } from '~/composables/useUpdater'
import { usePushNavigation } from '~/composables/usePushNavigation'
import { useRouter, useRoute } from 'vue-router'
import { getCurrentWindow } from '@tauri-apps/api/window'
import { isTauriRuntime } from '~/lib/platform/runtime'
import { DEBUG_WINDOW_LABEL } from '~/lib/debug/debugBus'
import { debugPublisher } from '~/lib/debug/debugPublisher'
import { speakingAttributePublisher } from '~/lib/livekit/speakingAttributePublisher'
import { deafenedAttributePublisher } from '~/lib/livekit/deafenedAttributePublisher'
import { markAppStartupDone } from '~/composables/useAppStartup'
import { isServiceUnavailableError } from '~/lib/platform/retry'
import { useGlobalHotkeys } from '~/composables/useGlobalHotkeys'
import { usePlatform } from '~/composables/usePlatform'

// Loaded on their own, after the first screen, instead of in the startup bundle: none of them is
// visible at launch, and the call UI alone pulled the whole LiveKit client (`livekit-client`,
// `lib/livekit/manager.ts`, VoiceCallView) plus emoji-mart into what had to load and run before
// anything could render.
const UserProfileDialog = defineAsyncComponent(() => import("~/components/voice-ui/UserProfile/UserProfileDialog.vue"))
const MobileCallOverlay = defineAsyncComponent(() => import("~/components/voice-ui/Chat/MobileCallOverlay.vue"))
const SharedEmojiPickerHost = defineAsyncComponent(() => import("~/components/chat/SharedEmojiPickerHost.vue"))
const ImageCropperHost = defineAsyncComponent(() => import("~/components/voice-ui/Avatar/ImageCropperHost.vue"))
const QuickSwitcher = defineAsyncComponent(() => import("~/components/voice-ui/QuickSwitcher.vue"))
const NotificationSettingsDialog = defineAsyncComponent(() => import("~/components/voice-ui/Notifications/NotificationSettingsDialog.vue"))
const NotificationTargetDrawer = defineAsyncComponent(() => import("~/components/voice-ui/Notifications/NotificationTargetDrawer.vue"))
const HiddenChannelsDrawer = defineAsyncComponent(() => import("~/components/voice-ui/Channels/HiddenChannelsDrawer.vue"))

const { isMobile } = usePlatform()

// App-wide keyboard shortcuts (Settings → «Горячие клавиши»).
useGlobalHotkeys()

const { checkAuth, getMe, registerPushToken } = useAuth()
const userStore = useUserStore()
const signalR = useGroupsSignalR()
const usersSignalR = useUsersSignalR()
const { applyStartupStatus } = useUserStatus()
const { listenForUpdates } = useUpdater()
const { startPushNavigationListener, processStartupPush } = usePushNavigation()
const router = useRouter()
const route = useRoute()

const showSplash = ref(true)

// How long to wait before asking for the profile again when the backend is unavailable at
// startup — on top of the retries `invoke()` has already made for each attempt.
const STARTUP_PROFILE_RETRY_MS = 5000

// An overloaded/restarting backend at startup is not a sign-out: the session is still there, the
// profile just can't be fetched yet. Treating it like any other failure dropped a signed-in user
// onto the login screen, so keep the splash up and keep trying instead. Anything other than a
// transient failure still propagates to the caller.
async function getMeWhenAvailable() {
  for (;;) {
    try {
      return await getMe()
    } catch (e) {
      if (!isServiceUnavailableError(e)) throw e
      await new Promise((resolve) => setTimeout(resolve, STARTUP_PROFILE_RETRY_MS))
    }
  }
}

onMounted(async () => {
  // Input/background tracking for «Нет на месте». It only reaches the server through the groups hub,
  // so running before sign-in (or across a logout) costs nothing.
  startIdleDetection()
  // The debug window is a second, independent webview (its own JS heap, its own empty Pinia
  // store) that Rust always creates pointed at this same SPA entry — it has no business
  // touching auth/SignalR/updates itself, it only renders whatever `debugPublisher` bridges
  // over from the main window's real state. See `lib/debug/debugBus.ts`. Routing it to
  // `/debug` is handled by `middleware/debug-window.global.ts`, which runs before any page
  // component mounts — doing it here instead raced with `index.vue`'s own `onMounted` redirect.
  // The debug window (and the updater's desktop-only auto-update flow) only exist under Tauri —
  // a plain browser tab never has a second webview, so both are skipped entirely there.
  if (isTauriRuntime()) {
    if (getCurrentWindow().label === DEBUG_WINDOW_LABEL) {
      showSplash.value = false
      markAppStartupDone()
      return
    }
    debugPublisher.start()
    listenForUpdates()
    // Attached before the session check below so a tap that lands mid-startup is still noticed —
    // the coordinator holds anything it can't act on yet until there's a session.
    void startPushNavigationListener()
  }

  speakingAttributePublisher.start()
  deafenedAttributePublisher.start()

  if (userStore.isInitialized) {
    showSplash.value = false
    markAppStartupDone()
    return
  }

  // The only session check at startup — `pages/index.vue` waits for this one (useAppStartup.ts)
  // instead of running its own in parallel. The splash stays up until the first real screen is on
  // (for a signed-in user landing on `/`, the navigation to `/dashboard` — which loads that page's
  // code — is awaited before hiding it), so the login page never shows underneath in between.
  try {
    const authenticated = await checkAuth()
    if (authenticated) {
      const user = await getMeWhenAvailable()
      userStore.setUser(user)
      signalR.connect()
      usersSignalR.connect()
      // Not awaited: the server-side presence update doesn't gate anything on screen, and waiting
      // for it held the splash up for another round trip (~110ms).
      void applyStartupStatus()
      void registerPushToken()
      if (route.path === '/') await router.replace('/dashboard')
      // Cold start from a notification tap: the payload has been sitting in `pending_push.json`
      // since before the webview existed — now that auth/profile are up, act on it.
      void processStartupPush()
    } else {
      // If we are not on the index page and not authenticated, redirect to index
      if (route.path !== '/') {
        await router.push('/')
      }
    }
  } catch (e) {
    console.error('Failed to initialize user session:', e)
    Sentry.captureException(e, { extra: { action: 'init_user_session' } })
    userStore.clearUser()
    if (route.path !== '/') {
      await router.push('/')
    }
  } finally {
    showSplash.value = false
    markAppStartupDone()
  }
})
</script>

<template>
  <main>
    <NuxtPage />
    <Sonner />
    <UserProfileDialog />
    <MobileCallOverlay />
    <!-- One app-wide emoji picker, built once during idle and teleported into whichever emoji
         popover is open — see useSharedEmojiPicker.ts. Mounted here rather than per chat page
         because both `pages/group/[id].vue` and `pages/m-groups/[id].vue` host emoji popovers. -->
    <SharedEmojiPickerHost />
    <!-- Crop step for avatar uploads (profile, group, bot) — see useImageCropper.ts. -->
    <ImageCropperHost />
    <!-- ⌘K / ⇧⌘O — see useGlobalHotkeys.ts. -->
    <QuickSwitcher />
    <!-- «Настройки уведомлений» of a group — see useNotificationSettingsDialog.ts. -->
    <NotificationSettingsDialog />
    <NotificationTargetDrawer v-if="isMobile" />
    <HiddenChannelsDrawer v-if="isMobile" />
    <Transition name="splash-fade">
      <SplashScreen v-if="showSplash" />
    </Transition>
  </main>
</template>

<style>
.splash-fade-leave-active {
  transition: opacity 0.35s ease;
}

.splash-fade-leave-to {
  opacity: 0;
}
</style>
